import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, UserPlus } from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { EmployeeDetails } from "./tabPages/employeeDetails";
import { SlotsDetails } from "./tabPages/slotsDetails";
import { toast } from "sonner";
import  ClientProfile  from "./tabPages/clientProfile";

const ClientDetails = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [showAttendant, setShowAttendant] = useState(false);
  const [attendantForm, setAttendantForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [savingAttendant, setSavingAttendant] = useState(false);

  const createAttendant = async (e) => {
    e.preventDefault();
    if (!clientDetails?.organization) {
      toast.error("Organization not loaded yet");
      return;
    }
    if (!attendantForm.name || !attendantForm.email || !attendantForm.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSavingAttendant(true);
    try {
      await axios.post(
        `${API}/master/organizations/${encodeURIComponent(clientDetails.organization)}/attendants`,
        attendantForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Attendant account created");
      setShowAttendant(false);
      setAttendantForm({ name: "", email: "", password: "", phone: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create attendant");
    } finally {
      setSavingAttendant(false);
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await axios.get(
        `${API}/parking/slots/client/${clientId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setSlots(response.data);
    } catch (error) {
      toast.error("Failed to load parking slots");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Fetch client details using clientId from URL params
    // For now, we'll use mock data
    fetchSlots();
    fetch(`${API}/master/clients/${clientId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        // Set client details in state
        setClientDetails(data);
      })
      .catch((err) => console.error(err));
  }, []);
  const handleAddSlot = async (e, file) => {
    e.preventDefault();
    if (!file) return;
    if (!clientDetails?.organization) {
      alert("Organization not available");
      return;
    }
    try {
      const form = new FormData();
      form.append("file", file, file.name);

      const res = await fetch(
        `${API}/master/organizations/${encodeURIComponent(clientDetails.organization)}/slots/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: form,
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to upload slots data");
      }

      const json = await res.json();
      toast.success(
        "Parking slot added successfully!" +
          "\n" +
          "created:" +
          json.created?.length +
          "  skipped:" +
          json.skipped?.length,
      );
      fetchSlots();
    } catch (error) {
      toast.error("Failed to add parking slot");
    }
  };
  const handleEmployeeUpload = async (file) => {
    if (!file) return;
    if (!clientDetails?.organization) {
      alert("Organization not available");
      return;
    }

    try {
      const form = new FormData();
      form.append("file", file, file.name);

      const res = await fetch(
        `${API}/master/organizations/${encodeURIComponent(clientDetails.organization)}/employees/bulk`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: form,
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to upload employee data");
      }

      const json = await res.json();
      toast.success(
        " employee added successfully!" +
          "\n" +
          "created:" +
          json.created?.length +
          "  skipped:" +
          json.skipped?.length,
      );

      // Refresh client details to show newly added users
      fetch(`${API}/master/clients/${clientId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      })
        .then((r) => r.json())
        .then((d) => setClientDetails(d))
        .catch(() => {});
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload employee data");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/master/AllClients")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to All Clients
        </Button>
        <div className="mb-12 fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                About Client -{" "}
                {clientDetails ? clientDetails.organization : "Unknown"}
              </h1>
              <p className="text-xl text-gray-600">
                View and manage all client organizations using ParkSmart.
              </p>
            </div>
            <Dialog open={showAttendant} onOpenChange={setShowAttendant}>
              <DialogTrigger asChild>
                <Button className="bg-[#1A3C34] hover:bg-[#15302a] text-white rounded-full">
                  <UserPlus className="mr-2" size={18} />
                  Add Attendant
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Create Attendant{clientDetails ? ` · ${clientDetails.organization}` : ""}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={createAttendant} className="space-y-4">
                  <Input
                    placeholder="Full name"
                    value={attendantForm.name}
                    onChange={(e) => setAttendantForm({ ...attendantForm, name: e.target.value })}
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={attendantForm.email}
                    onChange={(e) => setAttendantForm({ ...attendantForm, email: e.target.value })}
                  />
                  <Input
                    type="password"
                    placeholder="Temporary password"
                    value={attendantForm.password}
                    onChange={(e) => setAttendantForm({ ...attendantForm, password: e.target.value })}
                  />
                  <Input
                    placeholder="Phone (optional)"
                    value={attendantForm.phone}
                    onChange={(e) => setAttendantForm({ ...attendantForm, phone: e.target.value })}
                  />
                  <Button type="submit" disabled={savingAttendant} className="w-full bg-[#1A3C34] text-white">
                    {savingAttendant ? "Creating…" : "Create Attendant"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          {clientDetails ? (
            <div className="flex item-center justify-between">
              <p>
                <strong>Organization:</strong> {clientDetails.organization}
              </p>
              <p>
                <strong>Total Users:</strong> {clientDetails.total_users}
              </p>
              <p>
                <strong>Total Vehicles:</strong> {clientDetails.total_vehicles}
              </p>
              <p>
                <strong>Total Bookings:</strong> {clientDetails.total_bookings}
              </p>
              {/* Add more details as needed */}
            </div>
          ) : (
            <p>Loading client details...</p>
          )}
        </div>
        <Tabs defaultValue="employees" className="w-full mt-6 mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-8">
            <TabsTrigger value="employees">Employees</TabsTrigger>
            <TabsTrigger value="slots">Slots</TabsTrigger>
            <TabsTrigger value="profile">Update client profile</TabsTrigger>
          </TabsList>
          <TabsContent value="employees">
            <EmployeeDetails
              clientDetails={clientDetails}
              handleEmployeeUpload={handleEmployeeUpload}
            />
          </TabsContent>
          <TabsContent value="slots">
            <SlotsDetails
              clientDetails={clientDetails}
              slots={slots}
              fetchSlots={fetchSlots}
              handleAddSlot={handleAddSlot}
            />
          </TabsContent>
          <TabsContent value="profile">
            <ClientProfile
              clientDetails={clientDetails}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDetails;
